import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "Amazon「商品画像が要件を満たしていません」エラーの原因と解決策",
	description:
		"Amazon出品時に「商品画像が要件を満たしていません」が表示される全原因（背景色・解像度・ファイルサイズ・商品占有率）と、それぞれの解決手順を解説。",
	publishDate: "2026-03-16",
	slug: "amazon-image-requirements-not-met",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function AmazonImageRequirementsNotMet() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section className="mb-8">
						<p className="text-gray-700 leading-relaxed">
							「商品画像が要件を満たしていません」——このメッセージ、何度見ても戸惑いますよね。
							どこが要件を満たしていないのかがわかりにくく、いろいろ試してやっと解決……という方も多いはずです。
							この記事では、このエラーが発生する全パターンの原因と、それぞれの解決方法を一覧でまとめました。
						</p>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							エラーの原因：主に4つのパターン
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							Amazonがメイン画像に求める要件はすべて満たさないとエラーになります。
							特に多い原因は以下の4つです。
						</p>

						<div className="space-y-6">
							<div className="bg-red-50 border border-red-200 rounded-xl p-5">
								<h3 className="font-semibold text-red-800 mb-2">
									原因1: 背景が純白ではない
								</h3>
								<p className="text-gray-700 text-sm leading-relaxed">
									メイン画像の背景はRGB(255, 255,
									255)の純白のみ許可されています。
									「ほぼ白」に見えるグレー(250,250,250)やクリーム色も認識されてエラーになります。
									スマートフォンで白い紙の上で撮影した場合でも、照明の影響でRGB値が純白にならないことがあります。
								</p>
							</div>

							<div className="bg-red-50 border border-red-200 rounded-xl p-5">
								<h3 className="font-semibold text-red-800 mb-2">
									原因2: 解像度が小さすぎる
								</h3>
								<p className="text-gray-700 text-sm leading-relaxed">
									Amazonでは長辺500px以上が必須で、ズーム機能のためには長辺1,600px以上が必要です。
									それ以下の場合は「要件を満たしていません」と表示されることがあります。
									最大は10,000px以下です。
								</p>
							</div>

							<div className="bg-red-50 border border-red-200 rounded-xl p-5">
								<h3 className="font-semibold text-red-800 mb-2">
									原因3: 商品の占有率が85%未満
								</h3>
								<p className="text-gray-700 text-sm leading-relaxed">
									メイン画像は画像全体の85%以上が商品で占められている必要があります。
									余白が多すぎる・商品が小さく写っている場合はNGです。
									また、テキスト・ロゴ・透かし・縁取り・カラーブロックの追加も禁止されています。
								</p>
							</div>

							<div className="bg-red-50 border border-red-200 rounded-xl p-5">
								<h3 className="font-semibold text-red-800 mb-2">
									原因4: ファイル形式が非対応
								</h3>
								<p className="text-gray-700 text-sm leading-relaxed">
									対応形式はJPEG・TIFF・GIFの3種類です。
									PNGはサブ画像のみ対応しており、メイン画像には使えません。
									ファイルサイズは5MB以下が推奨です（上限は10MB）。
								</p>
							</div>
						</div>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">解決手順</h2>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>エラーの原因を特定する</strong>
								<p className="mt-1 ml-5 text-sm">
									セラーセントラルの「出品管理 →
									画像エラー」で具体的なエラー内容を確認します。
									複数の要件が同時に違反している場合もあります。
								</p>
							</li>
							<li>
								<strong>画像を修正する（背景・サイズ・占有率）</strong>
								<p className="mt-1 ml-5 text-sm">
									背景が問題の場合:
									AI背景除去ツールで背景を透明にし、RGB(255,255,255)の純白レイヤーを追加します。
									サイズが問題の場合: 画像を1,600px以上にリサイズします。
									占有率が問題の場合:
									商品が画像全体の85%以上になるようトリミングします。
								</p>
							</li>
							<li>
								<strong>JPEGで書き出し、再アップロード</strong>
								<p className="mt-1 ml-5 text-sm">
									修正後はJPEGで書き出し、セラーセントラルの「画像を管理」から再アップロードします。
									反映には最大24時間かかります。
								</p>
							</li>
						</ol>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							しろぬきやで一括解決する方法
						</h2>
						<p className="text-gray-700 leading-relaxed">
							背景除去・純白化・リサイズをすべて手動でやるのは、商品数が多いと相当な作業量になります。
							<strong>しろぬきや</strong>
							はブラウザ上で画像をドロップするだけで、
							AI背景除去＋Amazon規格(1,600px・純白背景)への自動変換が完了します。
							画像はサーバーに送信されないため、商品情報の外部流出も心配不要です。
						</p>
					</section>

					<CtaBlock />
					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
